// eslint-disable-next-line no-unused-vars
import React from 'react';
import styled from '@emotion/styled';
import { array, string, bool, object, func, oneOfType } from 'prop-types';
import { useTranslation } from 'react-i18next';
import uniqueId from 'lodash/uniqueId';
import {
  Loading,
  SkeletonPlaceholder,
} from '@carbon/react';
import { Package } from 'lucide-react';

import useNumberUtils from 'Hooks/useNumberUtils';
import Img from 'Containers/Image/Image';
import Price from 'Components/Price/Price';
import AddToCart from 'Components/AddToCart/AddToCart';
import DeliveryEstimate from 'Components/Product/DeliveryEstimate';

const StyledContainer = styled.div`
  .externalSupplier {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    background: #F5F5F5;
    border-radius: 5px;
    padding: 20px;
    margin: 10px 0;

    &.placeholder > div {
      width: 100%;
    }

    > div {
      flex: 1;
    }

    .preview {
      display: flex;
      flex: 3;
      margin-bottom: 0;

      .imageContainer {
        width: 100px;
        height: 100px;
        padding: 10px;
        margin-right: 10px;
        box-sizing: border-box;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: #fff;
      }

      .image {
        max-width: 90px;
        max-height: 90px;
        object-fit: contain;
      }

      .details {
        max-width: none;

        .supplierName {
          font-weight: 600;
          color: #3a3a3a;
        }
  
        .title {
          font-size: 16px;
          line-height: 20px;
          margin-bottom: 5px;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
  
        .stock {
          font-size: 14px;
          color: #3749EE;
        }
      }
    }

    .price {
      align-items: center;
    }
  }

  @media screen and (max-width: 1024px) {
    .externalSupplier {
      flex-direction: column;
      align-items: flex-start;

      .preview {
        flex-direction: row;
      }

      > .deliveryEstimate,
      > .price,
      > .addToCartContainer {
        margin-left: 110px;
      }

      .addToCartContainer {
        flex-direction: column;
        align-items: start;
        gap: 10px;
      }
    }
  }
`;

const ExternalSuppliers = ({
  mainProduct,
  products,
  currency,
  withVat,
  isLoading,
  onAddToCart,
  onChangeQuantity,
}) => {
  const { t } = useTranslation();
  const { formatNumber } = useNumberUtils();

  if (!products || products.length === 0) {
    return (
      <StyledContainer>
        { Array.from({ length: 2 }, () => (
          <div
            className="externalSupplier placeholder"
            key={ uniqueId('externalSupplier') }
          >
            <SkeletonPlaceholder />
          </div>
        )) }
      </StyledContainer>
    );
  }

  return (
    <StyledContainer>
      { products.map((product, index) => (
        <div className="externalSupplier" key={ product.id }>
          <div className="preview">
            <div className="imageContainer">
              <Img
                className="image"
                src={ mainProduct?.pictures?.at(0)?.urlThumbnailPicture }
                unloader={ <Package size={ 50 } strokeWidth={ 1.25 } /> }
                loader={ <Loading active withOverlay={ false } small /> }
                loading="lazy"
                alt={ mainProduct.name }
              />
            </div>
            <div className="details">
              <p className="supplierName">{ t('product.supplierStockOffer', { id: index + 1 }) }</p>
              <p className="title">{ mainProduct.title }</p>
              <p className="stock">
                { `${ product.stock } ${ product.stock === 1 ? t('products.product') : t('products.products') }` }
              </p>
            </div>
          </div>
          <DeliveryEstimate estimate={ product.deliveryTimeDays } isExternalSupplier />
          <Price
            price={ product.price }
            promoPrice={ product.price }
            tooltipAlign="top"
            withVat={ withVat }
            currency={ currency }
            displayVatInfo
            withTooltip
            tooltipContent={ `
              ${ t('products.greenStamp') }
              ${ formatNumber(product.greenStampPrice, { currency }) }
          ` }
          />
          <AddToCart
            id={ product.id }
            loading={ isLoading === product.id }
            disabled={ isLoading === product.id }
            value={ product.quantity }
            onClick={ () => onAddToCart(product) }
            trackingContext={ {
              location: 'external_suppliers',
              product_type: 'external_supplier',
              product_sku: product?.code,
            } }
            onChange={ quantity => onChangeQuantity(product.id, quantity) }
            max={ product.stock }
          />
        </div>
      )) }
    </StyledContainer>
  );
};

ExternalSuppliers.propTypes = {
  mainProduct: object,
  products: array,
  currency: string,
  withVat: bool,
  isLoading: oneOfType([bool, string]),
  onAddToCart: func,
  onChangeQuantity: func,
};

ExternalSuppliers.displayName = 'ExternalSuppliersComponent';

export default ExternalSuppliers;
